/**
 * Cronograma de monitoramento (datado) — ciclo completo.
 *
 * Fluxo: Cliente → Unidade → Data → Técnico → Área/Subárea → Tipo →
 * Dispositivos → Confirmar. Cliente e unidade obrigatórios; dispositivos
 * carregados SOMENTE do cliente+unidade selecionados (nunca mistura).
 * RASCUNHO pode ficar sem técnico; PROGRAMADO exige técnico e é a missão
 * entregue ao ASTRAL FIELD. Todo erro da API é exibido.
 */
import { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Eye,
  Edit2,
  Plus,
  XCircle,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  SCHEDULE_STATUS_STYLE,
  apiErrorMessage,
  countBy,
  idsWhere,
  inputCls,
  pruneSelection,
  toggleIds,
  typeLabel,
  useContextClientId,
  useHierarchy,
} from "./hierarchy";

const DM = "/device-monitoring";

const FREQUENCIES = [
  { value: "once", label: "Única" },
  { value: "daily", label: "Diária" },
  { value: "weekly", label: "Semanal" },
  { value: "biweekly", label: "Quinzenal" },
  { value: "monthly", label: "Mensal" },
];

const STATUS_FILTERS = [
  "RASCUNHO",
  "PROGRAMADO",
  "EM_EXECUCAO",
  "PARCIAL",
  "CONCLUIDO",
  "CANCELADO",
];

const fmtDate = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR") : "—";

function StatusBadge({ status, label }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded-full border text-xs font-medium ${
        SCHEDULE_STATUS_STYLE[status] || ""
      }`}
    >
      {label || status}
    </span>
  );
}

/* ====================================================================== */
/* FORMULÁRIO (criar / editar)                                            */
/* ====================================================================== */

function ScheduleForm({ editing, defaultClientId, onCancel, onSaved }) {
  const isEdit = Boolean(editing);
  const [form, setForm] = useState(() => ({
    client_id: editing?.client_id || defaultClientId || "",
    unit_id: editing?.unit_id || "",
    scheduled_date: editing?.scheduled_date || new Date().toISOString().slice(0, 10),
    name: editing?.name || "",
    frequency: editing?.frequency || "once",
    recurrence_count: 4,
    notes: editing?.notes || "",
  }));
  const [technicians, setTechnicians] = useState([]);
  const [techIds, setTechIds] = useState(editing?.technician_ids || []);
  const [areaIds, setAreaIds] = useState(new Set(editing?.area_ids || []));
  const [typeKeys, setTypeKeys] = useState(new Set(editing?.type_keys || []));
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [selected, setSelected] = useState(new Set(editing?.device_ids || []));
  const [saving, setSaving] = useState(false);
  const executedIds = useMemo(
    () =>
      new Set(
        (editing?.devices || [])
          .filter((d) => d.status === "EXECUTADO")
          .map((d) => d.device_id)
      ),
    [editing]
  );
  const started = executedIds.size > 0;

  const { clients, units, areas } = useHierarchy({
    clientId: form.client_id,
    unitId: form.unit_id,
  });

  // Técnicos AUTORIZADOS para a unidade (mesma regra do backend).
  useEffect(() => {
    setTechnicians([]);
    if (!form.client_id || !form.unit_id) return;
    api
      .get(`${DM}/technicians`, {
        params: { client_id: form.client_id, unit_id: form.unit_id },
      })
      .then(({ data }) => setTechnicians(data.items || []))
      .catch((err) =>
        toast.error(apiErrorMessage(err, "Erro ao carregar técnicos."))
      );
  }, [form.client_id, form.unit_id]);

  // Dispositivos: somente ativos + vinculados do cliente+unidade.
  useEffect(() => {
    setDevices([]);
    if (!form.client_id || !form.unit_id) return undefined;
    let current = true;
    setLoadingDevices(true);
    api
      .get(`${DM}/schedules/candidate-devices`, {
        params: { client_id: form.client_id, unit_id: form.unit_id },
      })
      .then(({ data }) => {
        if (!current) return; // resposta de outra unidade (troca rápida)
        const items = data.items || [];
        setDevices(items);
        // Seleção nunca contém dispositivo fora da lista do cliente/unidade.
        setSelected((prev) => pruneSelection(prev, items));
      })
      .catch((err) =>
        toast.error(apiErrorMessage(err, "Erro ao carregar dispositivos."))
      )
      .finally(() => current && setLoadingDevices(false));
    return () => {
      current = false;
    };
  }, [form.client_id, form.unit_id]);

  const visible = useMemo(
    () =>
      devices.filter(
        (d) =>
          (areaIds.size === 0 || areaIds.has(d.area_id)) &&
          (typeKeys.size === 0 || typeKeys.has(d.type_key))
      ),
    [devices, areaIds, typeKeys]
  );
  const typeCounts = useMemo(() => countBy(visible, "type_key"), [visible]);
  const areaCounts = useMemo(() => countBy(devices, "area_id"), [devices]);
  const unitTypes = useMemo(() => Object.keys(countBy(devices, "type_key")).sort(), [devices]);
  const selectedVisible = visible.filter((d) => selected.has(d.id)).length;

  const setLevel = (k) => (e) => {
    const value = e.target.value;
    if (k === "client_id") {
      setForm({ ...form, client_id: value, unit_id: "" });
      setTechIds([]);
    } else if (k === "unit_id") {
      setForm({ ...form, unit_id: value });
      setTechIds([]);
    }
    setAreaIds(new Set());
    setTypeKeys(new Set());
    setSelected(new Set());
  };

  const toggleSet = (setter, value) =>
    setter((prev) => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });

  const toggleDevice = (id) => {
    if (executedIds.has(id) && selected.has(id)) {
      toast.error("Dispositivo já monitorado não pode ser removido.");
      return;
    }
    setSelected((prev) => toggleIds(prev, [id], !prev.has(id)));
  };

  const selectGroup = (ids, on) => {
    const safe = on ? ids : ids.filter((id) => !executedIds.has(id));
    setSelected((prev) => toggleIds(prev, safe, on));
  };

  const toggleTech = (id) =>
    setTechIds((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));

  const problems = [
    !form.client_id && "cliente",
    !form.unit_id && "unidade",
    !form.scheduled_date && "data",
    selected.size === 0 && "dispositivos",
  ].filter(Boolean);

  const save = async (status) => {
    if (problems.length) {
      toast.error(`Informe: ${problems.join(", ")}.`);
      return;
    }
    if (status === "PROGRAMADO" && techIds.length === 0) {
      toast.error("Atribua pelo menos um técnico antes de programar o cronograma.");
      return;
    }
    // Áreas registradas no cronograma = áreas dos dispositivos escolhidos.
    const chosenAreas = [
      ...new Set(devices.filter((d) => selected.has(d.id)).map((d) => d.area_id)),
    ];
    const body = {
      client_id: form.client_id,
      unit_id: form.unit_id,
      name: form.name,
      scheduled_date: form.scheduled_date,
      frequency: form.frequency,
      recurrence_count: form.frequency === "once" ? 1 : Number(form.recurrence_count) || 1,
      selection_mode: "manual",
      area_ids: chosenAreas,
      subarea_ids: [],
      type_keys: [...typeKeys],
      device_ids: [...selected],
      technician_ids: techIds,
      status,
      notes: form.notes,
    };
    setSaving(true);
    try {
      const { data } = isEdit
        ? await api.patch(`${DM}/schedules/v2/${editing.id}`, body)
        : await api.post(`${DM}/schedules/v2`, body);
      const n = data.instances?.length || 1;
      toast.success(
        isEdit
          ? "Cronograma atualizado."
          : `Cronograma criado (${data.device_count} dispositivos${
              n > 1 ? `, ${n} instâncias` : ""
            }) — ${data.status_label || data.status}.`
      );
      onSaved(data, { recurrent: n > 1 });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro ao salvar o cronograma."));
    } finally {
      setSaving(false);
    }
  };

  const areaName = (id) => areas.find((a) => a.id === id)?.name || devices.find((d) => d.area_id === id)?.area_name || "—";

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-5" data-testid="schedule-form">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{isEdit ? "Editar cronograma" : "Novo cronograma"}</h3>
        <button onClick={onCancel} className="text-sm text-muted-foreground hover:text-foreground">
          Fechar
        </button>
      </div>
      {started && (
        <p className="text-xs text-amber-600">
          Cronograma iniciado: a data não pode ser alterada e os {executedIds.size}{" "}
          dispositivo(s) já monitorado(s) não podem ser removidos.
        </p>
      )}

      {/* 1-4: cliente, unidade, data, nome/recorrência */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <div>
          <label className="block text-sm mb-1">1. Cliente *</label>
          <select
            data-testid="schedule-client"
            value={form.client_id}
            onChange={setLevel("client_id")}
            disabled={isEdit}
            className={inputCls}
          >
            <option value="">Selecione o cliente…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1">2. Unidade *</label>
          <select
            data-testid="schedule-unit"
            value={form.unit_id}
            onChange={setLevel("unit_id")}
            disabled={isEdit || !form.client_id}
            className={inputCls}
          >
            <option value="">
              {form.client_id
                ? units.length ? "Selecione a unidade…" : "Cliente sem unidades"
                : "Selecione o cliente primeiro"}
            </option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1">3. Data *</label>
          <input
            type="date"
            data-testid="schedule-date"
            value={form.scheduled_date}
            disabled={started}
            onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-sm mb-1">Nome (opcional)</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Gerado: Unidade — data"
            className={inputCls}
          />
        </div>
        {!isEdit && (
          <>
            <div>
              <label className="block text-sm mb-1">Recorrência</label>
              <select
                data-testid="schedule-frequency"
                value={form.frequency}
                onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                className={inputCls}
              >
                {FREQUENCIES.map((f) => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </select>
            </div>
            {form.frequency !== "once" && (
              <div>
                <label className="block text-sm mb-1">Nº de ocorrências</label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={form.recurrence_count}
                  onChange={(e) => setForm({ ...form, recurrence_count: e.target.value })}
                  className={inputCls}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* 4. técnico(s) */}
      <div>
        <div className="text-sm mb-1">
          4. Técnico(s) — o primeiro marcado é o principal
          {form.unit_id && technicians.length === 0 && (
            <span className="ml-2 text-xs text-amber-600">
              Nenhum técnico autorizado para esta unidade.
            </span>
          )}
        </div>
        {!form.unit_id ? (
          <p className="text-xs text-muted-foreground">Selecione cliente e unidade.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="schedule-technicians">
            {technicians.map((t) => {
              const idx = techIds.indexOf(t.id);
              return (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => toggleTech(t.id)}
                  className={`px-3 py-1.5 rounded-md border text-sm ${
                    idx >= 0 ? "bg-primary text-primary-foreground border-primary" : "border-border"
                  }`}
                >
                  {t.name || t.email}
                  {idx === 0 && <span className="ml-1 text-xs opacity-80">(principal)</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* 5-6: área + tipo */}
      {form.unit_id && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <div className="text-sm mb-1">5. Área (filtro)</div>
            <div className="flex flex-wrap gap-2">
              {Object.keys(areaCounts).length === 0 && !loadingDevices && (
                <span className="text-xs text-muted-foreground">
                  Esta unidade não possui dispositivos ativos vinculados.
                </span>
              )}
              {Object.entries(areaCounts).map(([id, n]) => (
                <button
                  type="button"
                  key={id}
                  onClick={() => toggleSet(setAreaIds, id)}
                  className={`px-3 py-1.5 rounded-md border text-sm ${
                    areaIds.has(id) ? "bg-secondary border-primary" : "border-border"
                  }`}
                >
                  {areaName(id)} <span className="text-xs text-muted-foreground">({n})</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-sm mb-1">6. Tipo de dispositivo (filtro)</div>
            <div className="flex flex-wrap gap-2">
              {unitTypes.map((k) => (
                <button
                  type="button"
                  key={k}
                  onClick={() => toggleSet(setTypeKeys, k)}
                  className={`px-3 py-1.5 rounded-md border text-sm ${
                    typeKeys.has(k) ? "bg-secondary border-primary" : "border-border"
                  }`}
                >
                  {typeLabel(k)}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 7: dispositivos */}
      {form.unit_id && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">7. Dispositivos</span>
            <span className="text-muted-foreground">
              {Object.entries(typeCounts)
                .map(([k, n]) => `${typeLabel(k)}: ${n}`)
                .join(" · ") || "—"}
            </span>
            <span className="ml-auto font-medium" data-testid="schedule-selected-count">
              Selecionados: {selected.size}
              {selectedVisible !== selected.size && ` (${selectedVisible} nos filtros)`}
            </span>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <button
              type="button"
              data-testid="schedule-select-all"
              onClick={() => selectGroup(visible.map((d) => d.id), true)}
              className="px-2 py-1 rounded border border-border"
            >
              Selecionar todos ({visible.length})
            </button>
            <button
              type="button"
              onClick={() => selectGroup(visible.map((d) => d.id), false)}
              className="px-2 py-1 rounded border border-border"
            >
              Limpar visíveis
            </button>
            {Object.keys(typeCounts).map((k) => (
              <button
                type="button"
                key={k}
                onClick={() => selectGroup(idsWhere(visible, "type_key", k), true)}
                className="px-2 py-1 rounded border border-border"
              >
                + todos {typeLabel(k)}
              </button>
            ))}
            {areaIds.size !== 1 &&
              Object.keys(countBy(visible, "area_id")).map((a) => (
                <button
                  type="button"
                  key={a}
                  onClick={() => selectGroup(idsWhere(visible, "area_id", a), true)}
                  className="px-2 py-1 rounded border border-border"
                >
                  + área {areaName(a)}
                </button>
              ))}
          </div>
          {loadingDevices ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Carregando dispositivos…</div>
          ) : visible.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {devices.length === 0
                ? "Esta unidade ainda não possui dispositivos cadastrados."
                : "Nenhum dispositivo para os filtros selecionados."}
            </div>
          ) : (
            <div className="overflow-auto max-h-80 border border-border rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-muted-foreground sticky top-0">
                  <tr>
                    <th className="py-2 px-2 w-8"></th>
                    <th className="text-left py-2 px-2">Código</th>
                    <th className="text-left py-2 px-2">Tipo</th>
                    <th className="text-left py-2 px-2">Área</th>
                    <th className="text-left py-2 px-2">Subárea</th>
                    <th className="text-left py-2 px-2">Local</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((d) => (
                    <tr
                      key={d.id}
                      className="border-b border-border/50 cursor-pointer hover:bg-accent/40"
                      onClick={() => toggleDevice(d.id)}
                    >
                      <td className="py-1.5 px-2 text-center">
                        <input type="checkbox" readOnly checked={selected.has(d.id)} />
                      </td>
                      <td className="py-1.5 px-2 font-mono">
                        {d.code}
                        {executedIds.has(d.id) && (
                          <span className="ml-1 text-emerald-600">✓</span>
                        )}
                      </td>
                      <td className="py-1.5 px-2">{d.type_label || typeLabel(d.type_key)}</td>
                      <td className="py-1.5 px-2">{d.area_name}</td>
                      <td className="py-1.5 px-2">{d.subarea_name || "—"}</td>
                      <td className="py-1.5 px-2">{d.location || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm mb-1">Observações</label>
        <textarea
          rows={2}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className={inputCls}
        />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {problems.length > 0 && (
          <span className="text-xs text-muted-foreground mr-auto">
            Falta: {problems.join(", ")}
          </span>
        )}
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-md border border-border text-sm">
          Cancelar
        </button>
        {(!isEdit || editing.status === "RASCUNHO") && !started && (
          <button
            type="button"
            data-testid="schedule-save-draft"
            disabled={saving || problems.length > 0}
            onClick={() => save("RASCUNHO")}
            className="px-4 py-2 bg-secondary rounded-md text-sm font-medium disabled:opacity-50"
          >
            Salvar rascunho
          </button>
        )}
        <button
          type="button"
          data-testid="schedule-save-program"
          disabled={saving || problems.length > 0 || techIds.length === 0}
          title={techIds.length === 0 ? "Atribua pelo menos um técnico" : ""}
          onClick={() =>
            save(isEdit && !["RASCUNHO", "PROGRAMADO"].includes(editing.status) ? editing.status : "PROGRAMADO")
          }
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
        >
          {saving ? "Salvando…" : isEdit ? "Salvar e programar" : "Programar"}
        </button>
      </div>
    </div>
  );
}

/* ====================================================================== */
/* DETALHE                                                                */
/* ====================================================================== */

function ScheduleDetail({ id, onBack, onEdit, onCancelRequest }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [syncAudit, setSyncAudit] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get(`${DM}/schedules/v2/${id}`)
      .then(({ data: d }) => setData(d))
      .catch((err) => toast.error(apiErrorMessage(err, "Erro ao abrir o cronograma.")))
      .finally(() => setLoading(false));
    // Qualidade da sincronização FIELD ↔ ERP: somente gestão (403 para os demais).
    api
      .get(`${DM}/field/sync-audit`, { params: { schedule_id: id, include_rows: false } })
      .then(({ data: d }) => setSyncAudit(d))
      .catch((err) => {
        setSyncAudit(null);
        if (err?.response?.status !== 403) {
          toast.error(apiErrorMessage(err, "Erro na auditoria de sincronização."));
        }
      });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [id]);

  if (loading && !data) {
    return <div className="py-12 text-center text-muted-foreground">Carregando…</div>;
  }
  if (!data) {
    return (
      <div className="py-12 text-center">
        <button onClick={onBack} className="underline text-sm">Voltar</button>
      </div>
    );
  }
  const editable = !["CANCELADO", "CONCLUIDO"].includes(data.status);

  return (
    <div className="space-y-4" data-testid="schedule-detail">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Voltar
        </button>
        <h3 className="font-semibold ml-2">{data.name}</h3>
        <StatusBadge status={data.status} label={data.status_label} />
        <div className="ml-auto flex gap-2">
          <button onClick={load} className="p-2 rounded-md border border-border" title="Atualizar">
            <RefreshCw className="h-4 w-4" />
          </button>
          {editable && (
            <>
              <button onClick={() => onEdit(data)} className="flex items-center gap-1 px-3 py-2 rounded-md border border-border text-sm">
                <Edit2 className="h-4 w-4" /> Editar
              </button>
              <button onClick={() => onCancelRequest(data)} className="flex items-center gap-1 px-3 py-2 rounded-md border border-red-500/40 text-red-600 text-sm">
                <XCircle className="h-4 w-4" /> Cancelar
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 text-sm">
        {[
          ["Cliente", data.client_name],
          ["Unidade", data.unit_name],
          ["Data", fmtDate(data.scheduled_date)],
          ["Técnico(s)", (data.technician_names || []).join(", ") || "—"],
          ["Executados", `${data.executed_total}/${data.expected_total} (${data.coverage_pct}%)`],
          ["Sincronizados FIELD", data.field_synced],
        ].map(([k, v]) => (
          <div key={k} className="rounded-md border border-border p-3">
            <div className="text-xs text-muted-foreground">{k}</div>
            <div className="font-medium">{v}</div>
          </div>
        ))}
      </div>
      {data.status === "CANCELADO" && (
        <p className="text-sm text-red-600">
          Cancelado por {data.cancelled_by_name} em{" "}
          {data.cancelled_at ? new Date(data.cancelled_at).toLocaleString("pt-BR") : "—"}. Motivo:{" "}
          {data.cancel_reason}
        </p>
      )}
      <div className="flex flex-wrap gap-2 text-xs">
        {(data.by_area || []).map((a) => (
          <span key={a.area_id} className="px-2 py-1 rounded bg-secondary">
            {a.area_name}: {a.executed}/{a.expected}
          </span>
        ))}
      </div>

      {/* Progresso por TIPO — somente registros persistidos no ERP. */}
      {syncAudit && (
        <div className="rounded-lg border border-border p-3 space-y-2" data-testid="schedule-sync-quality">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-sm font-medium">Qualidade da sincronização (ASTRAL FIELD → ERP)</h4>
            {syncAudit.alert && (
              <span className="text-xs font-medium text-red-600">▲ {syncAudit.alert}</span>
            )}
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-sm">
            {[
              ["Esperados", syncAudit.quality.expected],
              ["Recebidos", syncAudit.quality.received],
              ["Falharam", syncAudit.quality.failed],
              ["Pendentes", syncAudit.quality.pending],
              ["Reconciliados", syncAudit.quality.reconciled],
              ["Última sincronização", syncAudit.quality.last_sync_at
                ? new Date(syncAudit.quality.last_sync_at).toLocaleString("pt-BR") : "—"],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-xs text-muted-foreground">{k}</div>
                <div className="font-medium">{v}</div>
              </div>
            ))}
          </div>
          {Object.keys(syncAudit.historical_rejections?.by_category || {}).length > 0 && (
            <div className="text-xs text-muted-foreground">
              Rejeições no histórico ({syncAudit.historical_rejections.attempts} tentativas,{" "}
              {syncAudit.historical_rejections.devices} dispositivos):{" "}
              {Object.entries(syncAudit.historical_rejections.by_category).map(([c, n]) => `${c} ${n}`).join(" · ")}
              {" · status enviados: "}
              {Object.entries(syncAudit.historical_rejections.by_status_sent).map(([c, n]) => `${c} (${n})`).join(", ")}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="schedule-types">
        {(data.types || []).map((t) => (
          <div key={t.type_key} className="rounded-md border border-border p-3 text-sm">
            <div className="font-medium">{t.label}</div>
            <div className="text-2xl font-semibold">
              {t.executed}/{t.count}
            </div>
            <div className="h-1.5 rounded bg-secondary mt-1 overflow-hidden">
              <div className="h-full bg-primary" style={{ width: `${t.coverage_pct}%` }} />
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {t.pending} pendente(s) · {t.coverage_pct}%
            </div>
          </div>
        ))}
      </div>

      {(data.sync_failures || []).length > 0 && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/5 p-3 space-y-2" data-testid="schedule-sync-failures">
          <div className="text-sm font-medium text-red-600">
            {data.sync_failures.length} resultado(s) enviado(s) pelo ASTRAL FIELD foram RECUSADOS pelo ERP
            (não contam como executados até o reenvio corrigido)
          </div>
          <div className="overflow-auto max-h-56">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="text-left py-1 px-2">Dispositivo</th>
                  <th className="text-left py-1 px-2">HTTP</th>
                  <th className="text-left py-1 px-2">Código</th>
                  <th className="text-left py-1 px-2">Motivo</th>
                  <th className="text-left py-1 px-2">Técnico</th>
                  <th className="text-left py-1 px-2">Quando</th>
                </tr>
              </thead>
              <tbody>
                {data.sync_failures.map((f) => (
                  <tr key={`${f.device_id}-${f.created_at}`} className="border-t border-border/50">
                    <td className="py-1 px-2 font-mono">{f.code || f.device_id}</td>
                    <td className="py-1 px-2">{f.http_status}</td>
                    <td className="py-1 px-2">{f.error_code}</td>
                    <td className="py-1 px-2">{f.detail}</td>
                    <td className="py-1 px-2">{f.user_name}</td>
                    <td className="py-1 px-2">{f.created_at ? new Date(f.created_at).toLocaleString("pt-BR") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-border bg-card overflow-auto max-h-[28rem]">
        <table className="w-full text-xs">
          <thead className="bg-muted/50 text-muted-foreground sticky top-0">
            <tr>
              <th className="text-left py-2 px-2">Código</th>
              <th className="text-left py-2 px-2">Tipo</th>
              <th className="text-left py-2 px-2">Área</th>
              <th className="text-left py-2 px-2">Subárea</th>
              <th className="text-left py-2 px-2">Status</th>
              <th className="text-left py-2 px-2">Resultado</th>
              <th className="text-left py-2 px-2">Técnico</th>
              <th className="text-left py-2 px-2">Origem</th>
              <th className="text-left py-2 px-2">Executado em</th>
            </tr>
          </thead>
          <tbody>
            {(data.devices || []).map((d) => (
              <tr key={d.device_id} className="border-b border-border/50">
                <td className="py-1.5 px-2 font-mono">{d.code}</td>
                <td className="py-1.5 px-2">{d.type_label}</td>
                <td className="py-1.5 px-2">{d.area_name}</td>
                <td className="py-1.5 px-2">{d.subarea_name || "—"}</td>
                <td className={`py-1.5 px-2 font-medium ${d.status === "EXECUTADO" ? "text-emerald-600" : "text-muted-foreground"}`}>
                  {d.status}
                </td>
                <td className="py-1.5 px-2">
                  {d.result_status
                    ? `${d.result_status.replace(/_/g, " ")}${d.capture ? ` · ${d.total}` : ""}`
                    : "—"}
                </td>
                <td className="py-1.5 px-2">{d.technician_name || "—"}</td>
                <td className="py-1.5 px-2">{d.sync_source || "—"}</td>
                <td className="py-1.5 px-2">
                  {d.executed_at ? new Date(d.executed_at).toLocaleString("pt-BR") : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ====================================================================== */
/* CANCELAMENTO                                                           */
/* ====================================================================== */

function CancelDialog({ schedule, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`${DM}/schedules/v2/${schedule.id}/cancel`, { reason });
      toast.success("Cronograma cancelado.");
      onDone();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro ao cancelar."));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-4 space-y-3" data-testid="schedule-cancel-dialog">
        <h4 className="font-semibold">Cancelar cronograma</h4>
        <p className="text-sm text-muted-foreground">
          {schedule.name} — {fmtDate(schedule.scheduled_date)}. O histórico é preservado.
        </p>
        <textarea
          rows={3}
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Motivo do cancelamento (obrigatório)"
          className={inputCls}
          data-testid="schedule-cancel-reason"
        />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md border border-border text-sm">Voltar</button>
          <button
            disabled={!reason.trim() || saving}
            onClick={submit}
            data-testid="schedule-cancel-confirm"
            className="px-4 py-2 rounded-md bg-red-600 text-white text-sm font-medium disabled:opacity-50"
          >
            {saving ? "Cancelando…" : "Confirmar cancelamento"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ====================================================================== */
/* LISTAGEM                                                               */
/* ====================================================================== */

export default function Schedule() {
  const contextClientId = useContextClientId();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientId, setClientId] = useState(contextClientId);
  const [unitId, setUnitId] = useState("");
  const [status, setStatus] = useState("");
  const [view, setView] = useState({ mode: "list" }); // list | form | detail
  const [cancelling, setCancelling] = useState(null);
  const { clients, units } = useHierarchy({ clientId });

  useEffect(() => {
    setClientId(contextClientId);
    setUnitId("");
  }, [contextClientId]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (clientId) params.client_id = clientId;
      if (unitId) params.unit_id = unitId;
      if (status) params.status = status;
      const { data } = await api.get(`${DM}/schedules/v2`, { params });
      setItems(data.items || []);
    } catch (err) {
      setItems([]);
      toast.error(apiErrorMessage(err, "Erro ao carregar cronogramas."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, unitId, status]);

  const openEdit = async (row) => {
    try {
      const { data } = await api.get(`${DM}/schedules/v2/${row.id}`);
      setView({ mode: "form", editing: data });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro ao abrir o cronograma."));
    }
  };

  const onSaved = (saved, { recurrent }) => {
    // Atualiza a listagem imediatamente (sem refresh manual).
    if (recurrent) {
      load();
    } else {
      setItems((prev) => [saved, ...prev.filter((i) => i.id !== saved.id)]);
    }
    setView({ mode: "detail", id: saved.id });
  };

  if (view.mode === "form") {
    return (
      <ScheduleForm
        editing={view.editing}
        defaultClientId={clientId}
        onCancel={() => setView(view.editing ? { mode: "detail", id: view.editing.id } : { mode: "list" })}
        onSaved={onSaved}
      />
    );
  }

  return (
    <div className="space-y-4">
      {cancelling && (
        <CancelDialog
          schedule={cancelling}
          onClose={() => setCancelling(null)}
          onDone={() => {
            setItems((prev) =>
              prev.map((i) => (i.id === cancelling.id ? { ...i, status: "CANCELADO", status_label: "Cancelado" } : i))
            );
            const reopen = view.mode === "detail" ? view.id : null;
            setCancelling(null);
            setView(reopen ? { mode: "detail", id: reopen, key: Date.now() } : { mode: "list" });
          }}
        />
      )}

      {view.mode === "detail" ? (
        <ScheduleDetail
          key={`${view.id}-${view.key || ""}`}
          id={view.id}
          onBack={() => {
            setView({ mode: "list" });
            load();
          }}
          onEdit={(d) => setView({ mode: "form", editing: d })}
          onCancelRequest={(d) => setCancelling(d)}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold mr-2">Cronogramas</h3>
            <select
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setUnitId("");
              }}
              className="px-3 py-2 rounded-md border border-border bg-background text-sm"
            >
              <option value="">Todos os clientes</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <select
              value={unitId}
              disabled={!clientId}
              onChange={(e) => setUnitId(e.target.value)}
              className="px-3 py-2 rounded-md border border-border bg-background text-sm disabled:opacity-50"
            >
              <option value="">Todas as unidades</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-3 py-2 rounded-md border border-border bg-background text-sm"
            >
              <option value="">Todos os status</option>
              {STATUS_FILTERS.map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
              ))}
            </select>
            <button
              data-testid="schedule-new"
              onClick={() => setView({ mode: "form" })}
              className="ml-auto flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium"
            >
              <Plus className="h-4 w-4" />
              Novo Cronograma
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CalendarDays className="h-8 w-8 mx-auto mb-2 opacity-50" />
              Nenhum cronograma {clientId ? "para este cliente" : "cadastrado"}.
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm" data-testid="schedule-table">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-muted-foreground">
                    <th className="text-left py-3 px-3">Data</th>
                    <th className="text-left py-3 px-3">Cliente</th>
                    <th className="text-left py-3 px-3">Unidade</th>
                    <th className="text-left py-3 px-3">Técnico</th>
                    <th className="text-right py-3 px-3">Dispositivos</th>
                    <th className="text-right py-3 px-3">Executados</th>
                    <th className="text-right py-3 px-3">Pendentes</th>
                    <th className="text-right py-3 px-3">%</th>
                    <th className="text-left py-3 px-3">Status</th>
                    <th className="text-right py-3 px-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => (
                    <tr key={s.id} className="border-b border-border/50">
                      <td className="py-2 px-3 whitespace-nowrap">{fmtDate(s.scheduled_date)}</td>
                      <td className="py-2 px-3">{s.client_name || "—"}</td>
                      <td className="py-2 px-3">{s.unit_name || "—"}</td>
                      <td className="py-2 px-3">
                        {s.technician || <span className="text-amber-600">Sem técnico</span>}
                        {(s.technician_names || []).length > 1 && (
                          <span className="text-xs text-muted-foreground"> +{s.technician_names.length - 1}</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right">{s.expected_total}</td>
                      <td className="py-2 px-3 text-right">{s.executed_total}/{s.expected_total}</td>
                      <td className="py-2 px-3 text-right">{s.pending_total}</td>
                      <td className="py-2 px-3 text-right">{Math.round(s.coverage_pct || 0)}%</td>
                      <td className="py-2 px-3">
                        <StatusBadge status={s.status} label={s.status_label} />
                      </td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        <button title="Abrir" onClick={() => setView({ mode: "detail", id: s.id })} className="p-1 text-muted-foreground hover:text-foreground">
                          <Eye className="h-4 w-4" />
                        </button>
                        {!["CANCELADO", "CONCLUIDO"].includes(s.status) && (
                          <>
                            <button title="Editar" onClick={() => openEdit(s)} className="p-1 text-muted-foreground hover:text-foreground">
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button title="Cancelar" onClick={() => setCancelling(s)} className="p-1 text-red-500/70 hover:text-red-600">
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
